/**
 * Date formatting for display.
 *
 * One place, because dates of birth were being rendered three different ways:
 * the raw ISO string the API returns (`1985-07-02`), a hand-rolled
 * `MM-DD-YYYY` with dashes, and whatever `toLocaleDateString` gave the
 * viewer's locale. The product shows dates of birth as MM/DD/YYYY.
 *
 * Note this is for *display* only. An `<input type="date">` must keep its
 * value in `yyyy-MM-dd` — that is the format the HTML spec requires, and the
 * browser already renders it in the user's locale. Do not run input values
 * through here.
 */

const pad = (n) => String(n).padStart(2, '0');

/**
 * MM/DD/YYYY, or `fallback` when there is no usable date.
 *
 * Accepts the `yyyy-MM-dd` string the API returns, a Date, or a timestamp.
 * Plain `yyyy-MM-dd` is split rather than passed to `new Date()`, which parses
 * a bare date string as UTC midnight and then renders it in local time — that
 * shifts the date back a day for anyone west of Greenwich, so a patient born
 * on the 2nd shows up as born on the 1st.
 */
export const formatDate = (value, fallback = '--') => {
  if (value === null || value === undefined || value === '') return fallback;

  if (typeof value === 'string') {
    const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoDate) {
      const [, year, month, day] = isoDate;
      return `${month}/${day}/${year}`;
    }
  }

  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return fallback;

  return `${pad(parsed.getMonth() + 1)}/${pad(parsed.getDate())}/${parsed.getFullYear()}`;
};

/** Whole years from a date of birth to today, or `fallback` if it is unusable. */
export const calculateAge = (value, fallback = '--') => {
  if (value === null || value === undefined || value === '') return fallback;

  let born;
  if (typeof value === 'string') {
    const isoDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    born = isoDate
      ? new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]))
      : new Date(value);
  } else {
    born = value instanceof Date ? value : new Date(value);
  }
  if (Number.isNaN(born.getTime())) return fallback;

  const today = new Date();
  let age = today.getFullYear() - born.getFullYear();
  // Not had this year's birthday yet.
  const monthDelta = today.getMonth() - born.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < born.getDate())) {
    age -= 1;
  }
  return age < 0 ? fallback : String(age);
};

export default formatDate;
